import { useCallback, useState } from 'react';

import { Button } from '../../../cdl/Button/Button';
import { Callout } from '../../../cdl/Callout/Callout';
import { Text } from '../../../cdl/Text/Text';
import { TextButton } from '../../../common/buttons/TextButton';
import { AdvancedError } from '../../../common/Error/AdvancedError';
import { formatCompanyName } from '../../../common/helpers/formatCompanyName.helper';
import { translate } from '../../../common/helpers/translate.helper';
import { LoadingIndicator } from '../../../common/LoadingIndicator/LoadingIndicator';
import { Box } from '../../../common/ui/Box';
import { useDemandDefinitionCalculationContext } from '../../hooks/useDemandDefinitionCalculationContext';
import type { DemandFleetChangeVessel } from '../../types/DemandFleetChangeVessel';

import { FleetChangeCompany } from './FleetChangeCompany';
import { FleetChangesSearchInput } from './FleetChangesSearchInput';

interface ExpansionState {
    scope: string;
    companyIds: Set<string>;
}

interface FleetChangesStepProps {
    isDeactivated: boolean;
}

export const FleetChangesStep = ({ isDeactivated }: FleetChangesStepProps) => {
    const [appliedSearchQuery, setAppliedSearchQuery] = useState('');
    const [clearCount, setClearCount] = useState(0);
    const [expansionState, setExpansionState] = useState<ExpansionState | null>(null);
    const {
        retainedCalculation: calculation,
        companies,
        isCalculationError,
        isCalculationFetching,
        retryCalculation,
    } = useDemandDefinitionCalculationContext();

    const applySearch = useCallback((value: string) => setAppliedSearchQuery(value), []);
    const clearSearch = useCallback(() => {
        setAppliedSearchQuery('');
        setClearCount((count) => count + 1);
    }, []);

    if (isCalculationError) {
        return (
            <Callout
                variant="negative"
                heading={translate('demandDefinition.calculationFailed')}
                description={translate('demandDefinition.calculationFailedDescription')}
            >
                <Button onClick={retryCalculation}>{translate('closelinkInsights.tryAgain')}</Button>
            </Callout>
        );
    }

    if (!calculation) {
        return (
            <Box display="flex" flex={1} alignItems="center" justifyContent="center">
                <LoadingIndicator />
            </Box>
        );
    }

    if (
        calculation.fleetChanges.addedVessels.length === 0 &&
        calculation.fleetChanges.deactivatedVessels.length === 0
    ) {
        if (isCalculationFetching) {
            return (
                <Box display="flex" flex={1} alignItems="center" justifyContent="center">
                    <LoadingIndicator />
                </Box>
            );
        }

        return (
            <Callout
                heading={translate('demandDefinition.noFleetChangesToReview')}
                description={translate('demandDefinition.fleetHasNoNewlyAddedOrDeactivatedVesselsToReview')}
            />
        );
    }

    const vessels = isDeactivated ? calculation.fleetChanges.deactivatedVessels : calculation.fleetChanges.addedVessels;
    const emptyDescription = translate(
        isDeactivated ? 'demandDefinition.noDeactivatedVesselsToReview' : 'demandDefinition.noNewlyAddedVesselsToReview'
    );
    const normalizedSearchQuery = appliedSearchQuery.trim().toLowerCase();
    const companyGroups = new Map<string, { name: string; vessels: DemandFleetChangeVessel[] }>();
    for (const vessel of vessels) {
        if (
            normalizedSearchQuery &&
            !vessel.vesselName.toLowerCase().includes(normalizedSearchQuery) &&
            !vessel.imo.includes(normalizedSearchQuery)
        ) {
            continue;
        }

        const existingGroup = companyGroups.get(vessel.customerId);
        if (existingGroup) {
            existingGroup.vessels.push(vessel);
        } else {
            const company = companies.find((entry) => entry.id === vessel.customerId);
            const name = formatCompanyName({ company });
            if (!name) {
                return <AdvancedError variant="fluid" />;
            }

            companyGroups.set(vessel.customerId, {
                name,
                vessels: [vessel],
            });
        }
    }
    const sortedCompanyGroups = [...companyGroups.entries()].sort(([, firstCompany], [, secondCompany]) =>
        firstCompany.name.localeCompare(secondCompany.name, 'en', { sensitivity: 'base' })
    );
    const visibleCompanyIds = sortedCompanyGroups.map(([companyId]) => companyId);
    const expansionScope = `${isDeactivated}:${clearCount}:${normalizedSearchQuery}`;
    let defaultExpandedCompanyIds: string[] = [];
    if (normalizedSearchQuery || (clearCount === 0 && vessels.length <= 10)) {
        defaultExpandedCompanyIds = visibleCompanyIds;
    } else if (clearCount === 0) {
        defaultExpandedCompanyIds = visibleCompanyIds.slice(0, 1);
    }
    const expandedCompanyIds =
        expansionState?.scope === expansionScope ? expansionState.companyIds : new Set(defaultExpandedCompanyIds);
    const allCompaniesExpanded =
        visibleCompanyIds.length > 0 && visibleCompanyIds.every((companyId) => expandedCompanyIds.has(companyId));

    const toggleCompany = (companyId: string) => {
        const companyIds = new Set(expandedCompanyIds);
        if (companyIds.has(companyId)) {
            companyIds.delete(companyId);
        } else {
            companyIds.add(companyId);
        }
        setExpansionState({ scope: expansionScope, companyIds });
    };

    const toggleAllCompanies = () => {
        setExpansionState({
            scope: expansionScope,
            companyIds: new Set(allCompaniesExpanded ? [] : visibleCompanyIds),
        });
    };

    return (
        <Box display="flex" flexDirection="column" gap={24}>
            <FleetChangesSearchInput onSearch={applySearch} onClear={clearSearch} />
            <Box display="flex" flexDirection="column" gap={8}>
                <Box display="flex" alignItems="center" justifyContent="space-between" gap={16}>
                    <Text variant="small" fontWeight={500}>
                        {translate('demandDefinition.vessels')}
                    </Text>
                    {visibleCompanyIds.length > 0 ? (
                        <TextButton type="button" onClick={toggleAllCompanies}>
                            {translate(
                                allCompaniesExpanded ? 'demandDefinition.collapseAll' : 'demandDefinition.expandAll'
                            )}
                        </TextButton>
                    ) : null}
                </Box>
                <Box display="flex" flexDirection="column" gap={8}>
                    {vessels.length === 0 ? (
                        <Callout description={emptyDescription} />
                    ) : sortedCompanyGroups.length === 0 ? (
                        <Callout description={translate('demandDefinition.noMatchingVessels')} />
                    ) : (
                        sortedCompanyGroups.map(([companyId, company]) => (
                            <FleetChangeCompany
                                key={`${isDeactivated}-${companyId}`}
                                name={company.name}
                                vessels={company.vessels}
                                calculation={calculation}
                                isDeactivated={isDeactivated}
                                isFetching={isCalculationFetching}
                                isOpen={expandedCompanyIds.has(companyId)}
                                onToggle={() => toggleCompany(companyId)}
                            />
                        ))
                    )}
                </Box>
            </Box>
        </Box>
    );
};
